import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Home, CheckCircle } from "lucide-react";
import { useNearbyCompletedJobs } from "@/hooks/useNearbyCompletedJobs";
import type { Block } from "@/types/proposals";

interface NeighborhoodMapBlockProps {
  block: Block;
  centerLat?: number;
  centerLng?: number;
  onUpdate?: (updates: any) => void;
  onSelect?: () => void;
}

export function NeighborhoodMapBlock({
  block,
  centerLat,
  centerLng,
  onUpdate,
  onSelect,
}: NeighborhoodMapBlockProps) {
  const radiusMiles = block.settings?.radiusMiles || 5;
  const { jobs, loading, count } = useNearbyCompletedJobs(
    centerLat || block.content?.centerLat || null,
    centerLng || block.content?.centerLng || null,
    radiusMiles
  );
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);

  const effectiveLat = centerLat || block.content?.centerLat;
  const effectiveLng = centerLng || block.content?.centerLng;

  useEffect(() => {
    if (!effectiveLat || !effectiveLng || typeof google === "undefined") return;

    const mapElement = document.getElementById(`neighborhood-map-${block.id}`);
    if (!mapElement || mapRef) return;

    const map = new google.maps.Map(mapElement, {
      center: { lat: effectiveLat, lng: effectiveLng },
      zoom: 13,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: [
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "off" }],
        },
      ],
    });

    setMapRef(map);
    setMapLoaded(true);

    // Add center marker (current property)
    new google.maps.Marker({
      position: { lat: effectiveLat, lng: effectiveLng },
      map,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        fillColor: "#ef4444",
        fillOpacity: 1,
        strokeWeight: 2,
        strokeColor: "#ffffff",
        scale: 10,
      },
      title: "This Property",
      zIndex: 100,
    });

    // Draw radius circle
    new google.maps.Circle({
      map,
      center: { lat: effectiveLat, lng: effectiveLng },
      radius: radiusMiles * 1609.34, // Convert miles to meters
      fillColor: "#3b82f6",
      fillOpacity: 0.1,
      strokeColor: "#3b82f6",
      strokeOpacity: 0.3,
      strokeWeight: 1,
    });
  }, [effectiveLat, effectiveLng, block.id, radiusMiles]);

  // Add job markers when jobs load
  useEffect(() => {
    if (!mapRef || !jobs.length) return;

    const infoWindow = new google.maps.InfoWindow();

    jobs.forEach((job, index) => {
      if (!job.latitude || !job.longitude) return;

      const marker = new google.maps.Marker({
        position: { lat: job.latitude, lng: job.longitude },
        map: mapRef,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: "#22c55e",
          fillOpacity: 1,
          strokeWeight: 2,
          strokeColor: "#ffffff",
          scale: 8,
        },
        title: job.property_address,
        zIndex: 50 - index,
      });

      marker.addListener("click", () => {
        infoWindow.setContent(`
          <div style="padding: 8px; max-width: 200px;">
            <p style="font-weight: 600; margin: 0 0 4px 0;">${job.property_address}</p>
            ${job.roof_type ? `<p style="font-size: 12px; color: #666; margin: 0;">${job.roof_type}</p>` : ""}
            <p style="font-size: 11px; color: #888; margin: 4px 0 0 0;">${job.distance_miles} miles away</p>
          </div>
        `);
        infoWindow.open(mapRef, marker);
      });
    });
  }, [mapRef, jobs]);

  const bgColor = block.style?.backgroundColor || "transparent";

  return (
    <div
      className="rounded-lg overflow-hidden"
      style={{ backgroundColor: bgColor }}
      onClick={onSelect}
    >
      {/* Header */}
      <div className="p-4 bg-gradient-to-r from-blue-500/10 to-green-500/10 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-green-500">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Neighborhood Portfolio</h3>
              <p className="text-sm text-muted-foreground">
                {loading ? "Loading..." : `${count} completed projects within ${radiusMiles} miles`}
              </p>
            </div>
          </div>
          {count > 0 && (
            <Badge className="bg-green-500 text-white gap-1">
              <CheckCircle className="w-3 h-3" />
              Trusted in Your Area
            </Badge>
          )}
        </div>
      </div>

      {/* Map */}
      <div
        id={`neighborhood-map-${block.id}`}
        className="w-full h-[300px] bg-muted"
      >
        {!effectiveLat && !effectiveLng && (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <div className="text-center">
              <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Map location not set</p>
            </div>
          </div>
        )}
      </div>

      {/* Projects List */}
      {!loading && jobs.length > 0 && (
        <div className="p-4 border-t">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {jobs.slice(0, 6).map((job) => (
              <div
                key={job.id}
                className="flex items-start gap-2 p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
              >
                {job.photo_url ? (
                  <img
                    src={job.photo_url}
                    alt="Project"
                    className="w-12 h-12 rounded object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded bg-muted flex items-center justify-center flex-shrink-0">
                    <Home className="w-5 h-5 text-muted-foreground" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium truncate">
                    {job.property_city || "Nearby"}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {job.distance_miles} mi • {job.roof_type || "Roofing"}
                  </p>
                </div>
              </div>
            ))}
          </div>
          {jobs.length > 6 && (
            <p className="text-center text-xs text-muted-foreground mt-3">
              + {jobs.length - 6} more completed projects
            </p>
          )}
        </div>
      )}

      {/* Empty State */}
      {!loading && jobs.length === 0 && effectiveLat && (
        <div className="p-6 text-center text-muted-foreground">
          <Home className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Be one of the first in this neighborhood!</p>
        </div>
      )}
    </div>
  );
}
